import { updateReviewPresentations } from 'generated/api-client';
import { useReviewCompanyPresentationsQuery } from 'generated/graphql';
import React, { FunctionComponent, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Header,
  Icon,
  Loader,
  Message,
  Modal,
} from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import { Review } from '../../types/review.type';
import { ProViewZones, ProViewZonesLegend } from '../ProViewZone';

/** Compares names whatever their case, accents or punctuation */
const normalize = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

type ReviewPresentationsFormProps = {
  review: Review;
  onCompleted: () => void;
};

export const ReviewPresentationsForm: FunctionComponent<
  ReviewPresentationsFormProps
> = ({ review, onCompleted }) => {
  const reviewPresentation = review.presentations[0]?.presentation;
  const companyId = reviewPresentation?.companyId;
  const { data, loading } = useReviewCompanyPresentationsQuery({
    variables: { companyId: companyId ?? 0 },
    skip: !companyId,
  });

  const before = useMemo(
    () => review.presentations.map(({ presentationId }) => presentationId),
    [review],
  );
  const [presentationIds, setPresentationIds] = useState<number[]>(before);
  const [submitting, setSubmitting] = useState(false);

  const presentations = data?.proPresentation ?? [];

  // The presentations of a company usually share its brand name: it is said once, above them,
  // and they differ by their trades. When their names differ, each one shows its own, and the
  // company name is above them.
  const sameName =
    new Set(presentations.map(({ name }) => normalize(name))).size <= 1;
  const heading = sameName
    ? presentations[0]?.name
    : reviewPresentation?.company?.name;

  const hasChanged =
    presentationIds.length !== before.length ||
    presentationIds.some((id) => !before.includes(id));

  const toggle = (id: number, checked: boolean): void => {
    setPresentationIds((ids) =>
      checked ? [...ids, id] : ids.filter((other) => other !== id),
    );
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await updateReviewPresentations({
        path: { reviewId: review.id },
        body: { presentationIds },
        throwOnError: true,
      });
      ToastSuccess('Succès', "Les fiches de l'avis sont à jour.");
      onCompleted();
    } catch (error) {
      const message = (error as { response?: { data?: { message?: unknown } } })
        ?.response?.data?.message;
      ToastError(
        'Erreur',
        typeof message === 'string'
          ? message
          : "Impossible de mettre à jour les fiches de l'avis",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title = <Modal.Header>Choisir les fiches de l&apos;avis</Modal.Header>;

  if (!companyId) {
    return (
      <>
        {title}
        <Modal.Content>
          <Message warning>
            La présentation de cet avis n&apos;est rattachée à aucune entreprise
            : ses fiches ne peuvent pas être modifiées.
          </Message>
        </Modal.Content>
      </>
    );
  }

  if (loading) {
    return (
      <>
        {title}
        <Modal.Content>
          <Loader active inline="centered" />
        </Modal.Content>
      </>
    );
  }

  return (
    <>
      {title}
      <Modal.Content>
        <Form onSubmit={submit}>
          {heading && <Header as="h5">Fiches de {heading}</Header>}
          {presentations.map((presentation) => {
            // The review stays visible on at least one presentation: the last checked one is locked
            const isLastChecked =
              presentationIds.length === 1 &&
              presentationIds[0] === presentation.id;
            return (
              <Form.Field key={presentation.id}>
                <Checkbox
                  checked={presentationIds.includes(presentation.id)}
                  disabled={isLastChecked}
                  onChange={(_, { checked }): void =>
                    toggle(presentation.id, !!checked)
                  }
                  label={
                    <label>
                      {presentation.mainTradeNames || presentation.name}
                      {sameName ? '' : ` · ${presentation.name}`}{' '}
                      <span style={{ marginLeft: '6px' }}>
                        <ProViewZones proViews={presentation.proViews} />
                      </span>
                    </label>
                  }
                />
              </Form.Field>
            );
          })}

          <ProViewZonesLegend />

          {/* Always shown, so that the modal keeps its size while checking */}
          <p
            style={{ color: '#767676', fontSize: '0.9em', fontStyle: 'italic' }}
          >
            <Icon name="info circle" />
            Un avis doit rester visible sur au moins une fiche.
          </p>

          <Button
            primary
            type="submit"
            loading={submitting}
            disabled={submitting || !hasChanged}
          >
            <Icon name="check" />
            Valider
          </Button>
        </Form>
      </Modal.Content>
    </>
  );
};
